import { useEffect, useState } from 'react';

interface UseHasEverBeenInViewOptions {
  /**
   * Ensure the element has been in view for at least this many milliseconds
   * to prevent loading tons of thumbnails during fast scrolling
   */
  forAtLeastMs?: number;
}

/**
 * Hook to detect if an element has ever been in the viewport
 * Once true, it stays true (useful for lazy loading that persists)
 */
export default function useHasEverBeenInView(
  element: HTMLElement | null,
  options: UseHasEverBeenInViewOptions = {}
): boolean {
  const { forAtLeastMs = 200 } = options;
  const [hasEverBeenInView, setHasEverBeenInView] = useState(false);

  useEffect(() => {
    // If already seen or no element, don't need to observe
    if (hasEverBeenInView || !element) {
      return;
    }

    let timeoutId: NodeJS.Timeout;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (entry.isIntersecting) {
          if (forAtLeastMs > 0) {
            // Wait for the specified time before marking as seen
            timeoutId = setTimeout(() => {
              setHasEverBeenInView(true);
            }, forAtLeastMs);
          } else {
            setHasEverBeenInView(true);
          }
        } else {
          // If element leaves viewport before timeout, cancel the timeout
          clearTimeout(timeoutId);
        }
      },
      {
        // Start loading when element is about to enter viewport
        rootMargin: '50px',
      }
    );

    observer.observe(element);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [element, hasEverBeenInView, forAtLeastMs]);

  return hasEverBeenInView;
}
